import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { RotateCcw } from "lucide-react";

/**
 * DailyNorthStarQuote — small quiet rotating quote line under the north-star
 * banner text. Deterministic per-day for the user; reroll on demand.
 */
export default function DailyNorthStarQuote() {
  const [quote, setQuote] = useState(null);
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/north-star-quote/today");
        if (data?.quote) setQuote(data.quote);
      } catch (e) { /* silent — this is a decorative surface */ }
    })();
  }, []);

  const reroll = async () => {
    if (rolling) return;
    setRolling(true);
    try {
      const { data } = await api.get(`/north-star-quote/reroll${quote?.id ? `?exclude_id=${quote.id}` : ""}`);
      if (data?.quote) setQuote(data.quote);
    } catch (e) { /* silent */ }
    finally {
      setTimeout(() => setRolling(false), 400);
    }
  };

  if (!quote) return null;

  return (
    <div
      className="mt-5 max-w-2xl fade-up"
      style={{ opacity: 0.7 }}
      data-testid="daily-north-star-quote"
      data-quote-theme={quote.theme || ""}
    >
      <div className="flex items-start gap-2">
        <p
          className="italic leading-snug flex-1"
          style={{
            color: "var(--text)",
            fontFamily: "var(--font-heading, serif)",
            fontSize: "clamp(0.95rem, 1.6vw, 1.1rem)",
          }}
          data-testid="daily-north-star-quote-text"
        >
          "{quote.quote_text}"
        </p>
        <button
          onClick={reroll}
          disabled={rolling}
          className="p-1.5 rounded-full transition-transform"
          style={{
            color: "var(--text-muted)",
            transform: rolling ? "rotate(360deg)" : "rotate(0deg)",
            transition: "transform 420ms cubic-bezier(0.4,0,0.2,1), opacity 200ms",
            opacity: 0.55,
          }}
          aria-label="Different quote"
          title="Different quote"
          data-testid="daily-north-star-quote-reroll"
        >
          <RotateCcw size={12} strokeWidth={1.75} />
        </button>
      </div>
      {quote.attribution && (
        <div
          className="text-right text-[10px] uppercase tracking-wider mt-0.5"
          style={{ color: "var(--text-muted)", fontFamily: "var(--font-body, sans-serif)" }}
          data-testid="daily-north-star-quote-attr"
        >
          — {quote.attribution}
        </div>
      )}
    </div>
  );
}
